import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Check, X, Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import { useCheckUsernameSignup } from "../../hooks/signupQueries";
import Footer from "../Login/Footer";

// Global Date Helpers
const isLeapYear = (year) => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

const getDaysInMonth = (month, year) => {
  const daysPerMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (!month || !year) return 31;
  const monthIndex = parseInt(month) - 1;
  let days = daysPerMonth[monthIndex];
  if (monthIndex === 1 && isLeapYear(parseInt(year))) days = 29;
  return days;
};

const API_URL = process.env.REACT_APP_API_URL;

const SignupUsernameInput = ({ register, errors, watch, onStatusChange }) => {
  const username = watch("username");
  const [checkStatus, setCheckStatus] = useState(null);
  const [message, setMessage] = useState("");
  const debounceTimer = useRef(null);

  // 1. Destructure mutateAsync directly.
  const { mutateAsync: checkUsername } = useCheckUsernameSignup();

  // 2. Use a Ref for the parent callback to avoid dependency loops
  const onStatusChangeRef = useRef(onStatusChange);
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (!username || username.trim() === "" || errors.username) {
      setCheckStatus(null);
      setMessage("");
      onStatusChangeRef.current?.(null);
      return;
    }

    setCheckStatus("checking");
    setMessage("Checking availability...");

    debounceTimer.current = setTimeout(async () => {
      try {
        // Use the stable function here
        const result = await checkUsername(username);

        if (result.available) {
          setCheckStatus("available");
          setMessage("Username is available");
          onStatusChangeRef.current?.("available");
        } else {
          const status =
            result.message.includes("characters") ||
            result.message.includes("contain")
              ? "invalid"
              : "taken";
          setCheckStatus(status);
          setMessage(result.message);
          onStatusChangeRef.current?.(status);
        }
      } catch (error) {
        setCheckStatus("error");
        setMessage("Error checking username");
        onStatusChangeRef.current?.("error");
      }
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
    // 3. 'checkUsername' is now stable, so it won't trigger loops
  }, [username, errors.username, checkUsername]);

  const getStatusIcon = () => {
    switch (checkStatus) {
      case "checking":
        return <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />;
      case "available":
        return <Check className="w-4 h-4 text-green-500" />;
      case "taken":
        return <X className="w-4 h-4 text-red-500" />;
      case "invalid":
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (checkStatus) {
      case "checking":
        return "border-blue-300 focus:ring-blue-500";
      case "available":
        return "border-green-300 focus:ring-green-500";
      case "taken":
      case "invalid":
      case "error":
        return "border-red-300 focus:ring-red-500";
      default:
        return errors.username ? "border-red-500" : "border-gray-300";
    }
  };

  const getMessageColor = () => {
    switch (checkStatus) {
      case "checking":
        return "text-blue-500";
      case "available":
        return "text-green-600";
      case "taken":
      case "invalid":
      case "error":
        return "text-red-600";
      default:
        return "text-gray-500";
    }
  };

  return (
    <div>
      <label className="block text-xs md:text-sm font-semibold text-gray-900 mb-2">
        Username
      </label>
      <div className="relative">
        <input
          type="text"
          placeholder="Username"
          autoComplete="off"
          {...register("username", {
            required: "Username is required",
            minLength: {
              value: 3,
              message: "Username must be at least 3 characters",
            },
            maxLength: {
              value: 30,
              message: "Username must be less than 30 characters",
            },
            pattern: {
              value: /^[a-zA-Z0-9._]+$/,
              message:
                "Username can only contain letters, numbers, dots and underscores",
            },
          })}
          className={`w-full px-4 md:px-5 py-2.5 md:py-3 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500 text-sm md:text-base transition-all ${getStatusColor()}`}
        />
        <div className="absolute inset-y-0 right-0 pr-3 md:pr-4 flex items-center pointer-events-none">
          {getStatusIcon()}
        </div>
      </div>

      {errors.username && (
        <p className="text-red-500 text-xs mt-2">{errors.username.message}</p>
      )}

      {!errors.username && checkStatus && message && (
        <p className={`text-xs ${getMessageColor()} mt-2`}>{message}</p>
      )}
    </div>
  );
};

const SignupStep1 = ({
  register,
  trigger,
  errors,
  watch,
  setValue,
  onSubmit,
  isLoading,
  error,
  usernameStatus,
  setUsernameStatus,
  showPassword,
  setShowPassword,
  isFormValid,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const watchDay = watch("birthDay");
  const watchMonth = watch("birthMonth");
  const watchYear = watch("birthYear");

  React.useEffect(() => {
    if (watchDay && watchMonth && watchYear) {
      trigger("birthYear");
    }
  }, [watchDay, watchMonth, watchYear, trigger]);

  const validDays = React.useMemo(() => {
    const maxDays = getDaysInMonth(watchMonth, watchYear);
    return Array.from({ length: maxDays }, (_, i) => i + 1);
  }, [watchMonth, watchYear]);

  React.useEffect(() => {
    if (watchDay && !validDays.includes(parseInt(watchDay))) {
      setValue("birthDay", "");
    }
  }, [validDays, watchDay, setValue]);

  const currentYear = new Date().getFullYear();
  const maxYear = currentYear - 18; // Only allow 18+ years old
  const yearOptions = Array.from({ length: maxYear - 1905 }, (_, i) => (
    <option key={i} value={maxYear - i}>
      {maxYear - i}
    </option>
  ));

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) || "Please enter a valid email address";
  };

  const isContinueDisabled =
    isLoading || !isFormValid || usernameStatus !== "available";

  return (
    <>
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm text-center">{error}</p>
        </div>
      )}

      <div className="space-y-4 md:space-y-5">
        <div>
          <input
            type="email"
            placeholder="email address"
            {...register("email", {
              required: "Email is required",
              validate: validateEmail,
            })}
            className={`w-full px-4 md:px-5 py-2.5 md:py-3 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500 text-sm md:text-base transition-all ${
              errors.email ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.email && (
            <p className="text-red-500 text-xs mt-2">{errors.email.message}</p>
          )}
        </div>

        <p className="text-xs md:text-sm text-gray-700">
          We may send important updates when needed.{" "}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              navigate("/policy", {
                state: { activeTab: "privacy", from: location.pathname },
              });
            }}
            className="text-blue-700 hover:underline font-medium bg-transparent border-none p-0 cursor-pointer"
          >
            Here’s why we ask for your contact information.
          </button>
        </p>

        <div>
          <label className="block text-xs md:text-sm font-semibold text-gray-900 mb-2">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters long",
                },
                maxLength: {
                  value: 50,
                  message: "Password cannot exceed 50 characters",
                },
              })}
              className={`w-full px-4 md:px-5 py-2.5 md:py-3 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500 text-sm md:text-base transition-all ${
                errors.password ? "border-red-500" : "border-gray-300"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 md:pr-4 flex items-center text-gray-500 hover:text-gray-700"
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          {errors.password && (
            <p className="text-red-500 text-xs mt-2">
              {errors.password.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs md:text-sm font-semibold text-gray-900 mb-2 md:mb-3">
            Date of birth
          </label>
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            <select
              {...register("birthDay", {
                required: "Day is required",
              })}
              className={`px-3 md:px-4 py-2 md:py-3 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 text-sm transition-all appearance-none cursor-pointer ${
                errors.birthDay ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Day</option>
              {validDays.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>

            <select
              {...register("birthMonth", {
                required: "Month is required",
              })}
              className={`px-3 md:px-4 py-2 md:py-3 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 text-sm transition-all appearance-none cursor-pointer ${
                errors.birthMonth ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Month</option>
              <option value="1">Jan</option>
              <option value="2">Feb</option>
              <option value="3">Mar</option>
              <option value="4">Apr</option>
              <option value="5">May</option>
              <option value="6">Jun</option>
              <option value="7">Jul</option>
              <option value="8">Aug</option>
              <option value="9">Sep</option>
              <option value="10">Oct</option>
              <option value="11">Nov</option>
              <option value="12">Dec</option>
            </select>

            <select
              {...register("birthYear", {
                required: "Year is required",
                validate: (value) => {
                  if (!value || !watchMonth || !watchDay) return true;

                  const birthDate = new Date(
                    parseInt(value),
                    parseInt(watchMonth) - 1,
                    parseInt(watchDay),
                  );

                  const today = new Date();
                  let age = today.getFullYear() - birthDate.getFullYear();
                  const m = today.getMonth() - birthDate.getMonth();

                  // Adjust age if birthday hasn't occurred yet this year
                  if (
                    m < 0 ||
                    (m === 0 && today.getDate() < birthDate.getDate())
                  ) {
                    age--;
                  }

                  return age >= 18 || "You must be at least 18 years old";
                },
              })}
              className={`px-3 md:px-4 py-2 md:py-3 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 text-sm transition-all appearance-none cursor-pointer ${
                errors.birthYear ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Year</option>
              {yearOptions}
            </select>
          </div>
          {(errors.birthDay || errors.birthMonth || errors.birthYear) && (
            <p className="text-red-500 text-xs mt-2">
              {errors.birthDay?.message ||
                errors.birthMonth?.message ||
                errors.birthYear?.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs md:text-sm font-semibold text-gray-900 mb-2">
            Full name
          </label>
          <input
            type="text"
            placeholder="Full Name"
            {...register("name", {
              required: "Full name is required",
              minLength: {
                value: 2,
                message: "Name must be at least 2 characters",
              },
              maxLength: {
                value: 100,
                message: "Name must be less than 100 characters",
              },
              validate: (value) =>
                value.trim() !== "" || "Name cannot be empty",
            })}
            className={`w-full px-4 md:px-5 py-2.5 md:py-3 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500 text-sm md:text-base transition-all ${
              errors.name ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.name && (
            <p className="text-red-500 text-xs mt-2">{errors.name.message}</p>
          )}
        </div>

        <SignupUsernameInput
          register={register}
          errors={errors}
          watch={watch}
          onStatusChange={setUsernameStatus}
        />

        <p className="text-xs md:text-sm text-gray-700">
          By tapping Sign up, you agree to our{" "}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              navigate("/policy", {
                state: { activeTab: "terms", from: location.pathname },
              });
            }}
            className="text-blue-700 hover:underline font-medium bg-none border-none p-0 cursor-pointer"
          >
            Terms
          </button>
          ,{" "}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              navigate("/policy", {
                state: { activeTab: "privacy", from: location.pathname },
              });
            }}
            className="text-blue-700 hover:underline font-medium bg-none border-none p-0 cursor-pointer"
          >
            Privacy Policy
          </button>{" "}
          and{" "}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              navigate("/policy", {
                state: { activeTab: "cookies", from: location.pathname },
              });
            }}
            className="text-blue-700 hover:underline font-medium bg-none border-none p-0 cursor-pointer"
          >
            Cookies Policy
          </button>
          .
        </p>

        <button
          onClick={onSubmit}
          disabled={isContinueDisabled}
          className={`w-full bg-[#b0d284] text-black font-semibold py-2.5 md:py-3 rounded-lg
            hover:bg-[#9ec170]
            active:scale-[0.98]
            focus:outline-none focus:ring-2 focus:ring-[#b0d284]/60
            transition-all duration-300 text-sm md:text-base
            ${isContinueDisabled ? "opacity-70 cursor-not-allowed hover:bg-[#b0d284] active:scale-100" : ""}
          `}
        >
          {isLoading ? "Validating..." : "Continue"}
        </button>

        <p className="text-center text-sm md:text-sm text-black">
          <a href="/" className="font-semibold">
            Log in
          </a>
        </p>
      </div>
    </>
  );
};

const SignupStep2 = ({
  email,
  onVerify,
  onBack,
  isLoading,
  error,
  formData,
}) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(90);
  const [stepError, setStepError] = useState(error || "");
  const [stepLoading, setStepLoading] = useState(isLoading || false);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (timer <= 0) {
      setCanResend(true);
      return;
    }
    const timeout = setTimeout(() => setTimer(timer - 1), 1000);
    return () => clearTimeout(timeout);
  }, [timer]);

  useEffect(() => {
    setStepError(error || "");
  }, [error]);

  useEffect(() => {
    setStepLoading(isLoading || false);
  }, [isLoading]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      setStepError("Please enter a valid 6-digit OTP");
      return;
    }

    setStepLoading(true);
    setStepError("");

    try {
      const verifyResponse = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email, otp: otpString }),
      });

      const verifyResult = await verifyResponse.json();

      if (!verifyResponse.ok || !verifyResult.success) {
        setStepError(verifyResult.message || "Invalid OTP");
        return;
      }

      onVerify(otpString, formData);
    } catch (error) {
      setStepError("Network error. Please try again.");
    } finally {
      setStepLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;
    setStepLoading(true);
    setStepError("");

    try {
      const response = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setStepError(result.message || "Failed to resend OTP");
        return;
      }

      setTimer(90);
      setCanResend(false);
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } catch (error) {
      setStepError("Network error. Please try again.");
    } finally {
      setStepLoading(false);
    }
  };

  const otpComplete = otp.every((digit) => digit !== "");

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="space-y-4 md:space-y-5">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-black font-semibold text-sm mb-4"
      >
        <ArrowLeft className="w-4 h-4 text-black" />
        Back to details
      </button>

      <div className="flex md:hidden items-center justify-center mb-2">
        <span className="wryven-font text-6xl text-black">Wryven</span>
      </div>

      <div className="text-center mb-6">
        <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
          Verify your email
        </h2>
        <p className="text-sm text-gray-600">
          We've sent a 6-digit code to{" "}
          <span className="font-medium">{email}</span>
        </p>
      </div>

      {stepError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm text-center">{stepError}</p>
        </div>
      )}

      <div className="flex gap-2 justify-center mb-6">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            maxLength="1"
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className="w-12 h-12 md:w-14 md:h-14 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
            placeholder="0"
            disabled={stepLoading}
          />
        ))}
      </div>

      <div className="text-center mb-6">
        {timer > 0 ? (
          <p
            className={`text-sm ${timer < 60 ? "text-red-600 font-bold" : "text-gray-600"}`}
          >
            Code expires in{" "}
            <span className="font-bold">{formatTime(timer)}</span>
          </p>
        ) : (
          <button
            type="button"
            onClick={handleResendOTP}
            disabled={stepLoading || !canResend}
            className="text-sm text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Resend OTP
          </button>
        )}
      </div>

      <button
        onClick={handleVerify}
        disabled={stepLoading || !otpComplete}
        className={`w-full bg-[#b0d284] text-black font-semibold py-2.5 md:py-3 rounded-lg
    hover:bg-[#9ec170]
    active:scale-[0.98]
    focus:outline-none focus:ring-2 focus:ring-[#b0d284]/60
    transition-all duration-300 text-sm md:text-base
    ${
      stepLoading || !otpComplete
        ? "opacity-70 cursor-not-allowed hover:bg-[#b0d284] active:scale-100"
        : ""
    }
  `}
      >
        {stepLoading ? (
          <span className="flex items-center justify-center">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-black"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Verifying...
          </span>
        ) : (
          "Verify and Create Account"
        )}
      </button>

      <p className="text-center text-xs md:text-sm text-black">
        Didn't receive the code?{" "}
        <button
          onClick={onBack}
          className="font-semibold text-[#6b8f3a] hover:text-[#5a7a30] transition-colors"
        >
          Try another email
        </button>
      </p>
    </div>
  );
};

const SignupForm = ({ onCompleteSignup, isLoading, error }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [step1Error, setStep1Error] = useState(null);
  const [formDataStep1, setFormDataStep1] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    setValidationError(error || null);
  }, [error]);
  
  // Stabilize the status setter to prevent infinite loops in the child useEffect
  const handleUsernameStatus = React.useCallback((status) => {
    setUsernameStatus(status);
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      email: "",
      password: "",
      birthDay: "",
      birthMonth: "",
      birthYear: "",
      name: "",
      username: "",
    },
  });

  const navigate = useNavigate();
  const handleStep1Submit = handleSubmit(async (data) => {
    setIsValidating(true);
    setStep1Error(null);

    try {
      const validateResponse = await fetch(
        `${API_URL}/api/auth/validate-signup`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: data.email,
            username: data.username,
            name: data.name,
            password: data.password,
            birthDay: data.birthDay,
            birthMonth: data.birthMonth,
            birthYear: data.birthYear,
          }),
        },
      );

      const validateResult = await validateResponse.json();

      if (!validateResponse.ok || !validateResult.success) {
        setStep1Error(
          validateResult.errors?.join(", ") ||
            validateResult.message ||
            "Validation failed",
        );
        return;
      }

      const otpResponse = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: data.email.toLowerCase().trim() }),
      });

      const otpResult = await otpResponse.json();

      if (!otpResponse.ok || !otpResult.success) {
        setStep1Error(otpResult.message || "Failed to send OTP");
        return;
      }

      setFormDataStep1(data);
      setCurrentStep(2);
    } catch (error) {
      setStep1Error("Network error. Please try again.");
    } finally {
      setIsValidating(false);
    }
  });

  const handleOtpVerify = async (otpCode, formData) => {
    setIsValidating(true);
    setValidationError(null);
    try {
      await onCompleteSignup({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        username: formData.username,
        birthDay: formData.birthDay,
        birthMonth: formData.birthMonth,
        birthYear: formData.birthYear,
        otp: otpCode,
      });
    } catch (error) {
      setValidationError(
        error.message || "Verification failed. Please try again.",
      );
      setCurrentStep(1);
    } finally {
      setIsValidating(false);
    }
  };

  const handleBackToStep1 = () => {
    setCurrentStep(1);
    setValidationError(null);
  };

  return (
    <div className="w-full bg-white flex flex-col min-h-screen">
      <nav className="hidden md:flex justify-between items-center py-3 px-8 sm:px-16 md:px-24 lg:px-40 xl:px-56 2xl:px-64 flex-shrink-0">
        <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate("/")}>
          <img
            src="/logos/Wryven-Feather-Gradient.png?v=1.0"
            alt="Wryven Logo"
            className="w-14 h-14 rounded-lg object-cover"
            loading="eager"
          />
          <span className="wryven-font text-5xl text-black">Wryven</span>
        </div>
        <a
          href="/"
          className="text-black font-medium hover:text-gray-700 transition-colors"
        >
          Log in
        </a>
      </nav>

      <main className="flex-1 flex justify-center items-center md:items-start pt-4 md:pt-8 px-4 md:px-0 md:pb-0">
        <div className="w-full max-w-lg">
          {currentStep === 1 && (
            <>
              {/* Mobile */}
              <div className="mb-6 md:mb-4 md:hidden">
                <div className="flex items-center justify-center mb-2">
                  <span className="wryven-font text-6xl text-black">
                    Wryven
                  </span>
                </div>

                <p className="text-center text-base text-black">
                  Sign up to share and discover Poetry, Shayari, Novels, Stories, Books, and more.
                </p>
              </div>

              {/* Desktop */}
              <div className="hidden md:block mb-4">
                <h1 className="text-4xl font-bold text-black leading-tight">
                  Sign up to explore writings, stories, Poetry, and more.
                </h1>
              </div>
            </>
          )}

          {currentStep === 1 && (
            <SignupStep1
              register={register}
              trigger={trigger}
              errors={errors}
              watch={watch}
              setValue={setValue}
              onSubmit={handleStep1Submit}
              isLoading={isValidating}
              error={step1Error}
              usernameStatus={usernameStatus}
              setUsernameStatus={handleUsernameStatus}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              isFormValid={isValid}
            />
          )}

          {currentStep === 2 && (
            <SignupStep2
              email={formDataStep1?.email}
              onVerify={handleOtpVerify}
              onBack={handleBackToStep1}
              isLoading={isValidating}
              error={validationError}
              formData={formDataStep1}
            />
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SignupForm;
